/**
 * useNodeActions — Common action handlers for agent node toolbars
 */

import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useCanvasOptional } from '../../CanvasContext'
import {
  api,
  getConfiguredApiBase,
  getAuthToken,
  getPublicationToken,
} from '../../../../api/client'
import type { C2paDownloadResult } from '../../../../api/c2paDownload'
import { useBooleanFlagValue } from '../../../../hooks/useBooleanFlagValue'
import { runC2paDownloadOnce } from './c2paDownloadProgress'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createNadDebug } from '../../../../utils/debugLog'
import { stripUrlQuery } from '../../../../utils/stripUrlQuery'
import {
  beginAssetDownload,
  downloadFormat,
  viewModeForPresentation,
  type AssetDownloadContext,
} from '../../../../analytics/assetDownloadAnalytics'
import { outputVariantId } from '../../../../analytics/outputVariantId'
import type { AssetDownloadEntryPoint, AssetDownloadTarget } from '../../../../analytics/generated/analyticsEvents'
import { noteNodeActions } from '../../../../analytics/nodeActionAnalytics'
import { useNodePresentation, type NodePresentation } from '../../NodePresentationContext'
import { buildDuplicateNodeName, newDuplicateTraceId, type DuplicateNodeMode } from '../../duplicateNodeData'

const assetDownloadLog = createNadDebug('dw:asset-download')
const nodeActionLog = createNadDebug('dw:canvas:node-actions')
const nodeDuplicateLog = createNadDebug('dw:node-duplicate:toolbar')

/** Lowercased file extension (no dot), or '' if none. */
export function fileExtension(filename: string): string {
  return downloadFormat(filename) ?? ''
}

export type AssetDownloadFilenameResolution =
  | 'preserved'
  | 'appended'
  | 'asset-extension-unavailable'

export interface ResolvedAssetDownloadFilename {
  filename: string
  resolution: AssetDownloadFilenameResolution
  assetExtension: string | null
}

/**
 * Keep a caller's meaningful attachment name, but restore the media extension
 * when legacy/generated node data stores a display label in `in_file_name`.
 */
export function resolveAssetDownloadFilename(
  assetPath: string,
  requestedFilename: string,
): ResolvedAssetDownloadFilename {
  const assetExtension = fileExtension(assetPath) || null
  if (fileExtension(requestedFilename)) {
    return { filename: requestedFilename, resolution: 'preserved', assetExtension }
  }
  if (!assetExtension) {
    return {
      filename: requestedFilename,
      resolution: 'asset-extension-unavailable',
      assetExtension: null,
    }
  }
  return {
    filename: `${requestedFilename}.${assetExtension}`,
    resolution: 'appended',
    assetExtension,
  }
}

/**
 * Returns true when the URL is already a self-authenticating signed asset
 * URL that should NOT be augmented with our backend's JWT / publication
 * token query params. Three shapes count:
 *
 *  - Legacy Supabase: ``*.supabase.co/storage/v1/object/sign/...`` —
 *    still in the wild during the RFC-0658 cutover window because YDoc
 *    may have cached signed URLs from before the env flip.
 *  - GCS v4: any URL with an ``X-Goog-Signature`` query param. GCS v4
 *    signs over the canonical query string, so adding our own params
 *    would invalidate the signature and the asset would 403.
 *  - Cloudflare R2: presigned URLs include ``Signature`` + ``Key-Pair-Id``
 *    pair (CloudFront-style). Defensive — ``r2_storage.py`` may mint
 *    these for showcase assets.
 */
export function isPresignedExternalUrl(u: URL): boolean {
  // Legacy Supabase signed URL shape.
  if (u.hostname.endsWith('.supabase.co') && u.pathname.includes('/storage/v1/object/sign/')) {
    return true
  }
  // GCS v4 signature.
  if (u.searchParams.has('X-Goog-Signature')) {
    return true
  }
  // Cloudflare R2 / CloudFront-style presigned.
  if (u.searchParams.has('Signature') && u.searchParams.has('Key-Pair-Id')) {
    return true
  }
  return false
}

/**
 * Build a download URL: inject the JWT (or publication token) only for the
 * configured API origin or same-origin routes, then add ?download=<filename>
 * so the storage backend responds with Content-Disposition: attachment.
 * External absolute URLs remain credential-free. Presigned URLs are returned
 * byte-for-byte unchanged because any query-string mutation invalidates their
 * signatures.
 */
export async function buildDownloadUrl(url: string, filename: string): Promise<string> {
  const u = new URL(url, window.location.origin)
  if (isPresignedExternalUrl(u)) return url

  const apiOrigin = new URL(getConfiguredApiBase(), window.location.origin).origin
  const isTrustedAssetOrigin =
    u.origin === window.location.origin || u.origin === apiOrigin
  if (isTrustedAssetOrigin) {
    if (!u.searchParams.get('token')) {
      const token = await getAuthToken()
      if (token) u.searchParams.set('token', token)
    }
    if (!u.searchParams.get('publication_token')) {
      const pubToken = getPublicationToken()
      if (pubToken) u.searchParams.set('publication_token', pubToken)
    }
  }
  u.searchParams.set('download', filename)
  return u.toString()
}

export function buildNodeAssetDownloadBaseUrl(projectId: string, assetPath: string): string {
  if (!assetPath) {
    throw new Error(`Cannot build download URL for empty asset path`)
  }
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return assetPath
  }
  const cleanPath = assetPath.replace(/^\/+/, '')
  const routePath = cleanPath.startsWith('assets/') ? cleanPath : `assets/${cleanPath}`
  return `${getConfiguredApiBase()}/projects/${projectId}/${routePath}`
}

export type AssetDownloadSource =
  | 'image-history-popover'
  | 'node-asset'
  | 'variants-panel'

interface TriggerAssetDownloadOptions {
  projectId: string
  assetPath: string
  filename: string
  source: AssetDownloadSource
  nodeId?: string
  /**
   * RFC-1577 `asset_download_action` context. When present the attempt records
   * `initiated` as it starts the handoff and `failed` if the handoff throws.
   * `projectId` / `nodeId` / `filename` are taken from the options above.
   */
  analytics?: Omit<AssetDownloadContext, 'projectId' | 'nodeId' | 'filename'>
}

/**
 * RFC-1577 entry point for a download source. A node's own download button is
 * the node toolbar on the canvas and `list_view` on the simplified view's
 * cards; variants surfaces are variant cards wherever they render.
 */
export function assetDownloadEntryPoint(
  source: AssetDownloadSource,
  presentation: NodePresentation,
): AssetDownloadEntryPoint {
  if (source === 'variants-panel' || source === 'image-history-popover') return 'variant_card'
  return presentation === 'list' || presentation === 'detail' ? 'list_view' : 'node_toolbar'
}

function assetDownloadTarget(source: AssetDownloadSource): AssetDownloadTarget {
  return source === 'node-asset' ? 'node_output' : 'variant'
}

/**
 * One id per download ATTEMPT, so the requested → navigation-created → failed
 * events of a single click can be correlated without colliding with the user's
 * next click on the same tile. `nodeId` and `source` stay as their own fields —
 * the trace id is for grouping, not for carrying identity.
 */
let downloadTraceSequence = 0

export function newDownloadTraceId(): string {
  downloadTraceSequence += 1
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `asset-download:${crypto.randomUUID()}`
  }
  // `crypto.randomUUID` needs a secure context, so plain-http dev origins land
  // here. `performance.now()` alone is not enough: browsers clamp its
  // resolution (coarser still with privacy protections on), so two attempts in
  // the same tick would share an id — exactly the collision this function
  // exists to prevent. The sequence makes the fallback unique by construction.
  return `asset-download:${performance.now().toString(36)}-${downloadTraceSequence}`
}

/** Log-only source: the "Download all clips" modal does not go through `triggerAssetDownload`. */
export type AssetDownloadLogSource = AssetDownloadSource | 'bulk-download'

interface AssetDownloadLogInput {
  event: string
  traceId: string
  source: AssetDownloadLogSource
  assetPath: string
  filename: string
  projectId?: string
  nodeId?: string
  extra?: Record<string, string | null>
}

/**
 * The ONLY way a `dw:asset-download` payload is built. Centralized so the
 * `asset_path` redaction cannot be forgotten at a call site: `assetPath` may be
 * an absolute presigned URL whose query string IS the credential, and a log
 * sink is not a place to keep one.
 *
 * Pure and exported so the redaction contract is testable without mocking the
 * debug sink — cross-file sink mocking is unsafe here, because this suite runs
 * under `isolate: false` and sibling files import this module unmocked.
 */
export function buildAssetDownloadLogPayload({
  event,
  traceId,
  source,
  assetPath,
  filename,
  projectId,
  nodeId,
  extra,
}: AssetDownloadLogInput): Record<string, string | null> & { event: string } {
  return {
    trace_id: traceId,
    ...(projectId === undefined ? {} : { project_id: projectId }),
    node_id: nodeId ?? null,
    source,
    asset_path: stripUrlQuery(assetPath),
    download_filename: filename,
    ...extra,
    // Last, so a stray `extra` key can never displace the event discriminator.
    event,
  }
}

/**
 * Start a browser-managed attachment download without fetching asset bytes in
 * JavaScript. Production asset routes redirect to signed storage URLs; a
 * credentialed fetch cannot follow that wildcard-CORS redirect. Hidden iframe
 * navigation preserves the backend's `Content-Disposition: attachment`
 * contract and keeps auth in the query string for media navigation.
 */
export async function triggerAssetDownload({
  projectId,
  assetPath,
  filename,
  source,
  nodeId,
  analytics,
}: TriggerAssetDownloadOptions): Promise<void> {
  const resolvedFilename = resolveAssetDownloadFilename(assetPath, filename)
  const effectiveFilename = resolvedFilename.filename
  const resolution = resolvedFilename.resolution === 'appended'
    ? 'extension_appended'
    : resolvedFilename.resolution === 'preserved'
      ? 'extension_preserved'
      : 'asset_extension_unavailable'
  const filenameFields = {
    requested: filename,
    resolved: effectiveFilename,
    requested_filename: filename,
    resolved_filename: effectiveFilename,
  }
  const traceId = newDownloadTraceId()
  const logBase = {
    traceId,
    source,
    assetPath,
    filename: effectiveFilename,
    projectId,
    nodeId,
  }
  assetDownloadLog(
    buildAssetDownloadLogPayload({
      ...logBase,
      event: 'asset-download-requested',
      extra: { ...filenameFields, phase: 'request-received' },
    }),
  )
  assetDownloadLog(
    buildAssetDownloadLogPayload({
      ...logBase,
      event: 'asset-download-filename-extension-resolved',
      extra: {
        ...filenameFields,
        phase: 'filename-resolution',
        result: resolution,
        resolution,
        extension: resolvedFilename.assetExtension ? `.${resolvedFilename.assetExtension}` : null,
        extension_source: resolvedFilename.assetExtension ? 'asset_path' : null,
      },
    }),
  )

  // RFC-1577 asset_download_action: one `initiated` per attempt, as the
  // handoff starts; `failed` names the stage that threw.
  const attempt = analytics
    ? beginAssetDownload({ ...analytics, projectId, nodeId, filename: effectiveFilename })
    : null
  let stage: 'missing_asset' | 'url_build_failed' | 'handoff_failed' = 'missing_asset'
  try {
    const url = buildNodeAssetDownloadBaseUrl(projectId, assetPath)
    stage = 'url_build_failed'
    const downloadUrl = await buildDownloadUrl(url, effectiveFilename)
    stage = 'handoff_failed'
    const iframe = document.createElement('iframe')
    iframe.style.display = 'none'
    iframe.src = downloadUrl
    iframe.dataset.assetDownloadSource = source
    document.body.appendChild(iframe)
    assetDownloadLog(
      buildAssetDownloadLogPayload({
        ...logBase,
        event: 'asset-download-dispatched',
        extra: {
          ...filenameFields,
          phase: 'browser-handoff',
          result: 'iframe_navigation_started',
        },
      }),
    )
    window.setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe)
    }, 60_000)
  } catch (error) {
    attempt?.failed(stage)
    assetDownloadLog(
      buildAssetDownloadLogPayload({
        ...logBase,
        event: 'asset-download-failed',
        extra: { error_name: error instanceof Error ? error.name : typeof error },
      }),
    )
    throw error
  }
}

/** RFC-1612: runtime flag gating download-time C2PA signing. */
export const C2PA_DOWNLOAD_SIGNING_FLAG = 'c2pa_download_signing_enabled'

/**
 * Only stored project MP4s that DreamWrite generated can be signed:
 * - the APNB C2PA endpoint accepts `video/mp4` only;
 * - absolute URLs are not project assets the API can read;
 * - user uploads (any `uploads/` directory, e.g. `videos/uploads/`) are never
 *   signed, because the manifest says the video was created by AI. The API
 *   applies the same rule (`routes/c2pa_download.py::is_user_upload`).
 */
export function isC2paSignableDownload(assetPath: string, filename: string): boolean {
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) return false
  if (isUserUploadPath(assetPath)) return false
  return fileExtension(assetPath) === 'mp4' || fileExtension(filename) === 'mp4'
}

/** True when a directory segment (not the file name) is `uploads`. */
export function isUserUploadPath(assetPath: string): boolean {
  return assetPath.split('/').slice(0, -1).includes('uploads')
}

/** RFC-1612: how often the console asks whether the video worker has signed the copy. */
export const C2PA_DOWNLOAD_POLL_INTERVAL_MS = 2_000
/**
 * RFC-1612: how long a download waits for signing before falling back to the
 * original. Covers queueing, a worker cold start and the worker's 60s signing budget.
 */
export const C2PA_DOWNLOAD_POLL_TIMEOUT_MS = 120_000

interface ResolveC2paDownloadPathOptions {
  projectId: string
  assetPath: string
  traceId: string
  source: AssetDownloadLogSource
  filename: string
  nodeId?: string
  /** Requests the signed copy (may queue signing on the video worker). */
  prepare: (projectId: string, assetPath: string) => Promise<C2paDownloadResult>
  /** Reads the status of a pending request. */
  poll: (projectId: string, assetPath: string) => Promise<C2paDownloadResult>
  pollIntervalMs?: number
  timeoutMs?: number
  sleep?: (ms: number) => Promise<void>
  now?: () => number
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms))

/**
 * Ask the API for a C2PA-signed copy and return the path to download. Signing
 * runs on the video worker, so a `pending` answer is polled until it is signed,
 * unsigned, or `timeoutMs` passes.
 *
 * Best effort and silent by design: when signing is unavailable or fails, the
 * backend logs a warning and the user simply gets the original file. Nothing
 * here surfaces to the user; the node shows only a loading state while this
 * runs (see `c2paDownloadProgress`).
 */
export async function resolveC2paDownloadPath({
  projectId,
  assetPath,
  traceId,
  source,
  filename,
  nodeId,
  prepare,
  poll,
  pollIntervalMs = C2PA_DOWNLOAD_POLL_INTERVAL_MS,
  timeoutMs = C2PA_DOWNLOAD_POLL_TIMEOUT_MS,
  sleep = defaultSleep,
  now = Date.now,
}: ResolveC2paDownloadPathOptions): Promise<string> {
  const logBase = { traceId, source, assetPath, filename, projectId, nodeId }
  assetDownloadLog(buildAssetDownloadLogPayload({ ...logBase, event: 'asset-download-c2pa-requested' }))
  const startedAt = now()
  try {
    let result = await prepare(projectId, assetPath)
    let polls = 0
    while (result.status === 'pending') {
      if (now() - startedAt >= timeoutMs) {
        assetDownloadLog(
          buildAssetDownloadLogPayload({
            ...logBase,
            event: 'asset-download-c2pa-timeout',
            extra: { job_id: result.jobId ?? '', polls: String(polls) },
          }),
        )
        return assetPath
      }
      await sleep(pollIntervalMs)
      result = await poll(projectId, assetPath)
      polls += 1
    }
    assetDownloadLog(
      buildAssetDownloadLogPayload({
        ...logBase,
        event: `asset-download-c2pa-${result.status}`,
        extra: { reason: result.reason, cached: String(result.cached), polls: String(polls) },
      }),
    )
    return result.status === 'signed' ? result.assetPath : assetPath
  } catch (error) {
    assetDownloadLog(
      buildAssetDownloadLogPayload({
        ...logBase,
        event: 'asset-download-c2pa-failed',
        extra: { error_name: error instanceof Error ? error.name : typeof error },
      }),
    )
    return assetPath
  }
}

/** Typed custom events for cross-component communication */
/** A node a "Send Feedback" report is about (canvas node right-click menu). */
export interface FeedbackContextNode {
  id: string
  /** Frontend node type id (analyticsNodeType spelling). */
  type: string
}

interface AppEventMap {
  /** `source` says where the request came from (analytics); omitted = Add to chat. */
  'add-node-to-chat': { nodeId: string; source?: 'add_to_chat' | 'upload' }
  /** Open the workspace Feedback modal, optionally about specific nodes. */
  'open-feedback': { nodes: FeedbackContextNode[] }
}

export function dispatchAppEvent<K extends keyof AppEventMap>(
  type: K,
  detail: AppEventMap[K],
) {
  window.dispatchEvent(new CustomEvent(type, { detail }))
}

export function listenAppEvent<K extends keyof AppEventMap>(
  type: K,
  handler: (detail: AppEventMap[K]) => void,
): () => void {
  const listener = (event: Event) => {
    if (event instanceof CustomEvent) {
      handler(event.detail)
    }
  }
  window.addEventListener(type, listener)
  return () => window.removeEventListener(type, listener)
}

interface UseNodeActionsOptions {
  nodeId: string
}

interface DuplicateActionOptions {
  traceId?: string
}

export function useNodeActions({ nodeId }: UseNodeActionsOptions) {
  const { t } = useTranslation('canvas')
  const canvas = useCanvasOptional()
  const projectId = useProjectId()
  const presentation = useNodePresentation()
  const c2paSigningEnabled = useBooleanFlagValue(C2PA_DOWNLOAD_SIGNING_FLAG, false)

  const handleDelete = useCallback(() => {
    nodeActionLog({
      event: 'toolbar-delete-requested',
      feature: 'canvas:node-actions',
      nodeId,
    })
    // Route through the confirmation flow when it's wired into the canvas.
    // `requestNodeDelete` always returns true when present (it either opens the
    // confirmation dialog or intentionally no-ops), so we only fall back to the
    // direct `removeNode` when the confirmation handler is absent entirely —
    // never as a post-rejection bypass of the confirm dialog.
    if (canvas?.requestNodeDelete) {
      canvas.requestNodeDelete(nodeId, 'toolbar')
      return
    }
    canvas?.removeNode(nodeId)
  }, [canvas, nodeId])

  const handleDuplicate = useCallback((mode: DuplicateNodeMode, originalName: string, options?: DuplicateActionOptions) => {
    const name = buildDuplicateNodeName(originalName, new Date())
    const traceId = options?.traceId ?? newDuplicateTraceId()
    nodeActionLog({
      event: 'toolbar-duplicate-requested',
      feature: 'canvas:node-actions',
      nodeId,
    })
    nodeDuplicateLog({
      event: 'toolbar-duplicate-requested',
      feature: 'node-duplicate',
      trace_id: traceId,
      projectId,
      sourceNodeId: nodeId,
      mode,
      sourceName: originalName,
      copyName: name,
      nameTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    const copyId = canvas?.duplicateNode(nodeId, { mode, name, traceId })
    // RFC-1577 canvas_node_action: logged once the copy's YDoc add is observed.
    if (copyId) noteNodeActions([copyId], 'duplicate', 'node_toolbar')
  }, [canvas, nodeId, projectId])

  const handleDownload = useCallback(async (
    assetPath: string,
    filename: string,
    source: AssetDownloadSource = 'node-asset',
    /** History index of the variant being downloaded (variants surfaces). */
    variantIndex?: number,
  ) => {
    if (!projectId) {
      assetDownloadLog(buildAssetDownloadLogPayload({
        event: 'asset-download-rejected',
        traceId: newDownloadTraceId(),
        source,
        assetPath,
        filename,
        nodeId,
        extra: { reason: 'missing-project-context' },
      }))
      toast.error(t('nodeActions.downloadFailedNoProject'))
      return
    }
    let downloadPath = assetPath
    if (c2paSigningEnabled && isC2paSignableDownload(assetPath, filename)) {
      // The owning node shows a loading state while this runs. A repeat click
      // on the same asset while it is still signing is ignored (null).
      const resolved = await runC2paDownloadOnce(assetPath, nodeId, () =>
        resolveC2paDownloadPath({
          projectId,
          assetPath,
          traceId: newDownloadTraceId(),
          source,
          filename,
          nodeId,
          prepare: (pid, path) => api.prepareC2paDownload(pid, path),
          poll: (pid, path) => api.getC2paDownloadStatus(pid, path),
        }),
      )
      if (resolved === null) return
      downloadPath = resolved
    }
    try {
      // RFC-1577 asset_download_action (supersedes canvas_video_download_initiated):
      // recorded inside the handoff, once per attempt, for every media type.
      await triggerAssetDownload({
        projectId,
        assetPath: downloadPath,
        filename,
        source,
        nodeId,
        analytics: {
          entryPoint: assetDownloadEntryPoint(source, presentation),
          target: assetDownloadTarget(source),
          viewMode: viewModeForPresentation(presentation),
          variantId: outputVariantId(nodeId, variantIndex),
        },
      })
    } catch (err) {
      console.error('Download failed', err)
      // Preserve message-bearing non-Error failures (raw string throws or plain
      // { message } objects carrying backend/orchestrator prose) instead of
      // collapsing them to the generic fallback. Control-flow narrowing on the
      // caught value keeps this assertion-free (no `as`, no `unknown` keyword).
      let message: string
      if (err instanceof Error) {
        message = err.message
      } else if (typeof err === 'string') {
        message = err
      } else if (
        typeof err === 'object' &&
        err !== null &&
        'message' in err &&
        typeof err.message === 'string'
      ) {
        message = err.message
      } else {
        message = t('nodeActions.unknownError')
      }
      toast.error(t('nodeActions.downloadFailed', { message }))
    }
  }, [projectId, t, nodeId, presentation, c2paSigningEnabled])

  const handleAddToChat = useCallback(() => {
    nodeActionLog({
      event: 'toolbar-add-to-chat-requested',
      feature: 'canvas:node-actions',
      nodeId,
    })
    dispatchAppEvent('add-node-to-chat', { nodeId })
  }, [nodeId])

  // Save-to-Library lives in the dedicated `useSaveToLibrary` hook, which owns
  // the loading/success/error visual-feedback state machine. Keeping a second
  // no-feedback implementation here previously allowed a stale branch to
  // silently revert the wired toolbars (PR #1086 clobbering PR #1095), so this
  // hook intentionally does NOT expose handleSaveToLibrary — there is one
  // source of truth.

  return { handleDelete, handleDuplicate, handleDownload, handleAddToChat }
}
